import closeBtn from 'Assets/images/close-btn.svg';
import uploadIcon from 'Assets/images/upload.svg';
import { ChatFooter } from 'Components/Chat/ChatFooter';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { useAttachments } from 'Components/Chat/hooks/attachments/useAttachments';
import { ContentArea } from 'Components/shared/ContentArea';
import { EMPTY_TIMEUUID, SHOW_FILE_UPLOAD } from 'Constants/env';
import { MobXProviderContext, observer } from 'mobx-react';
import type { ConversationModel } from 'Models/index';
import { ConversationEmptyState } from 'Modules/conversation/components/ConversationEmptyState';
import { ChatConversationsEmptyState } from 'Modules/conversation/listConversation/components/chatConversationsEmptyState';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { useUIStore } from 'Modules/ui/index.store';
import React from 'react';
import { FileDrop } from 'react-file-drop';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router';
import { Button, Grid, Modal } from 'semantic-ui-react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { routeChecks } from '../../utils/routeUtils';
import { Styled } from './index.styles';

export const ContextPanel: React.FC = observer(() => {
  const {
    conversation: {
      RecentConversations,
      CurrentConversation,
      adHocGroupParticipants,
    },
    message: { createMessagePost, editMessage },
    phoneCall: { AnyPhoneConnectionActive },
    ui: {
      fileDeleteModal,
      setEmojiPickerState,
      setFileDeletePopup,
      setMoreActionOptions,
      setSelectedParticipants,
      setShowDropDownBox,
      showDropDownBox,
    },
    person: { IsLoggedIn },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const { view } = React.useContext(ChatLayoutContext);
  const { entitySidePanelInfo } = useUIStore();
  const isDialpadActive = useSoftphoneStore((state) => state.isDialpadActive);

  const { conversationId } = useParams();
  const navigate = useNavigate();
  const {
    conversationsList,
    pinnedConversations,
    hasAttemptedConversationsFetch,
  } = useConversationStore();

  const { addAttachments, resetAttachments, getUploadedS3ObjectsMap } =
    useAttachments();

  const [shiftPressed, setShiftPressed] = React.useState(false);

  const isGroupConversation = React.useRef<boolean>();

  React.useEffect(() => {
    isGroupConversation.current =
      CurrentConversation?.grouping === 'Group' ||
      CurrentConversation?.grouping === 'Channel';
  }, [CurrentConversation?.grouping]);

  const handleCloseModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEmojiPickerState({ isEmojiPickerOpen: false, isEditingEmoji: false });
    setSelectedParticipants(null);
    setMoreActionOptions({ conversationId: '', show: false });
  };

  const getFirstFromHistory = (): ConversationModel =>
    RecentConversations.slice().sort((a, b) => {
      const aDate = a.lastMessageDate || a.created;
      const bDate = b.lastMessageDate || b.created;
      return aDate <= bDate ? 1 : -1; // 1 is B before A, -1 is A before B
    })[0];

  const handleShiftDown = (e) => {
    (e.keyCode === 16 || e.charCode === 16) && setShiftPressed(true);
  };

  const handleShiftUp = (e, state?: boolean) => {
    (e.keyCode === 16 || e.charCode === 16 || state) && setShiftPressed(false);
  };

  const handleOnDragOver = (e) => {
    if (!showDropDownBox.show) {
      setShowDropDownBox({ show: true, draggedOn: true });
      // Notify other components that file drag has started
      window.dispatchEvent(new CustomEvent('file-drag-start'));
    }
    document.addEventListener('keydown', handleShiftDown);
    document.addEventListener('keyup', handleShiftUp);
  };

  const handleOnDragLeave = () => {
    showDropDownBox.draggedOn &&
      setShowDropDownBox({ show: false, draggedOn: false });
    document.removeEventListener('keydown', handleShiftDown);
    document.removeEventListener('keyup', handleShiftUp);
    setShiftPressed(false);
  };

  const handleOnDrop = async (files: FileList, event) => {
    setShowDropDownBox({ show: false, draggedOn: false });
    await addAttachments(files);
    const uploadedS3Objects = getUploadedS3ObjectsMap();

    if (shiftPressed) {
      handleShiftUp(event, true);
      setTimeout(() => {
        void createMessagePost(
          CurrentConversation.id,
          null,
          null,
          CurrentConversation.grouping,
          uploadedS3Objects
        );
        resetAttachments();
      }, 1000);
    }

    document.removeEventListener('keydown', handleShiftDown);
    document.removeEventListener('keyup', handleShiftUp);
    return;
  };

  const handleCloseFileDrop = () => {
    setShowDropDownBox({ show: false, draggedOn: false });
    document.removeEventListener('keydown', handleShiftDown);
    document.removeEventListener('keyup', handleShiftUp);
  };

  const closeFileDeletePopup = () =>
    setFileDeletePopup({ show: false, externalId: '', messageId: '' });

  const handleDeleteConfirm = () =>
    editMessage(CurrentConversation.id, fileDeleteModal.messageId, null);

  const isRightSidebarOpened = () =>
    !AnyPhoneConnectionActive && !isDialpadActive && !entitySidePanelInfo;

  const largeColumns = isRightSidebarOpened() ? 8 : null;
  const smallColumns = isRightSidebarOpened() ? 4 : null;

  const renderContextContent =
    ['/chat', '/chat/menu'].some((path) => window.location.pathname === path) &&
    !!RecentConversations;

  const firstFromRecentHistroy = getFirstFromHistory();

  const isAdhocConversation =
    window.location.pathname.includes('adHocGroup') && adHocGroupParticipants;

  if (renderContextContent && firstFromRecentHistroy) {
    void navigate(`/chat/conversations/${firstFromRecentHistroy?.id}/menu`);
    return <Styled.EmptyPanel />;
  }

  if (window.location.pathname === '/directory') return;

  const currentConversationId = getCurrentConversationId();

  const shouldShowEmptyState =
    conversationsList?.length === 0 &&
    pinnedConversations?.length === 0 &&
    hasAttemptedConversationsFetch &&
    !currentConversationId &&
    !routeChecks.isNewConversationPage();

  const isSharedFilesAndLinksView = view === 'shared-files-and-links';
  const shouldShowFileDrop =
    window.location.pathname.includes('conversations') &&
    SHOW_FILE_UPLOAD &&
    !isSharedFilesAndLinksView;

  return (
    <Grid.Column
      id="context-panel"
      onClick={handleCloseModal}
      widescreen={largeColumns}
      computer={largeColumns}
      desktop={largeColumns}
      tablet={smallColumns}
      mobile={smallColumns}
      className="semantic-flex-column maxheightpercent"
    >
      <ContentArea>
        {shouldShowEmptyState && <ChatConversationsEmptyState />}

        {!shouldShowEmptyState && IsLoggedIn && (
          <>
            {shouldShowFileDrop && (
              //@ts-ignore
              <FileDrop
                className={showDropDownBox.show ? 'z-index-file-target' : ''}
                onFrameDragEnter={handleOnDragOver}
                onDragLeave={handleOnDragLeave}
                onDrop={handleOnDrop}
                onTargetClick={handleCloseFileDrop}
              >
                {showDropDownBox.show && (
                  <div
                    className={
                      showDropDownBox.draggedOn ? 'drag-drop-file' : ''
                    }
                  >
                    <img src={uploadIcon} />
                    <span>You can add comments before sending.</span>
                    <span>Hold shift to share immediately.</span>
                  </div>
                )}
              </FileDrop>
            )}
            {fileDeleteModal && (
              <Modal
                open={fileDeleteModal.show}
                size="small"
                id="confirmation-delete-modal"
                dimmer="blurring"
                closeOnDimmerClick
                centered
                onClose={closeFileDeletePopup}
                className="confirmation-modal"
              >
                <img
                  className="close-btn"
                  src={closeBtn}
                  onClick={closeFileDeletePopup}
                />
                <div className="semantic-flex-row">
                  Are you sure you want to delete this file?
                </div>
                <div className="semantic-flex-row">
                  <Button
                    primary
                    content="CANCEL"
                    onClick={closeFileDeletePopup}
                  />
                  <Button
                    secondary
                    content="DELETE"
                    onClick={handleDeleteConfirm}
                  />
                </div>
              </Modal>
            )}

            {isAdhocConversation ? (
              <>
                <ConversationEmptyState
                  isContactSelected={!!adHocGroupParticipants?.length}
                  className="mt-26"
                />
                <ChatFooter />
              </>
            ) : (
              <>
                {conversationId === EMPTY_TIMEUUID && <Navigate to="/chat/*" />}
                <Outlet />
              </>
            )}
          </>
        )}
      </ContentArea>
    </Grid.Column>
  );
});
